import { useState, useMemo } from "react";
import { Box, Button, Typography, Card, CircularProgress } from "@mui/material";
import { Payment, Close } from "@mui/icons-material";
import GenericModal from "../GenericModal";
import SubscriptionSelectionStep from "../PublicRegistration/SubscriptionSelectionStep";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Subscription } from "../../Utils/Types";
import { generatePaymentUrl } from "../../Utils/PaymentUtils";
import { PAYMENT_TYPES } from "../../Utils/Constants";
import { EWidths } from "../../Utils/Enums";
import styles from "./RenewSubscriptionModal.module.css";

interface RenewSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  currentSubscription: Subscription | null;
  availableSubscriptions: Subscription[];
  clientData?: any;
  loading?: boolean;
}

export default function RenewSubscriptionModal({
  open,
  onClose,
  currentSubscription,
  availableSubscriptions,
  clientData,
  loading = false,
}: RenewSubscriptionModalProps) {
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Derived values
  const priceDifference = useMemo(() => {
    if (!selectedSubscription || !currentSubscription) return 0;
    return Math.max(0, selectedSubscription.price - currentSubscription.price);
  }, [selectedSubscription, currentSubscription]);

  const isUpgrade = useMemo(() => {
    if (!selectedSubscription || !currentSubscription) return false;
    return selectedSubscription._id !== currentSubscription._id;
  }, [selectedSubscription, currentSubscription]);

  const handleClose = () => {
    setSelectedSubscription(null);
    onClose();
  };

  const handleSelectSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
  };

  const handleConfirmRenewal = async () => {
    if (!selectedSubscription || !clientData) return;

    setIsProcessing(true);
    try {
      // Mark this as subscription renewal payment type
      localStorage.setItem("paymentType", PAYMENT_TYPES.SUBSCRIPTION_RENEWAL);

      // Initiate payment
      const result = await generatePaymentUrl({
        clientId: clientData._id,
        clientData,
        selectedSubscription,
        orderDescription: `${
          isUpgrade
            ? Vocabulary.upgradeSubscription
            : Vocabulary.renewSubscription
        } - ${selectedSubscription.name}`,
        purpose: PAYMENT_TYPES.SUBSCRIPTION_RENEWAL,
        metadata: { subscriptionId: selectedSubscription._id },
      });

      // Handle case where payment is skipped (amount is 0)
      if (result?.paymentSkipped && result?.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (error) {
      console.error("Error initiating subscription renewal:", error);
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <GenericModal
        open={open}
        onClose={handleClose}
        title={Vocabulary.loading}
      >
        <Box className={styles.loadingContainer}>
          <CircularProgress size={40} />
          <Typography className={styles.loadingText}>
            {Vocabulary.loading}
          </Typography>
        </Box>
      </GenericModal>
    );
  }

  const modalActions = (
    <div className={styles.modalActions}>
      <Button
        variant="outlined"
        color="error"
        fullWidth
        onClick={handleClose}
        disabled={isProcessing}
        startIcon={<Close />}
      >
        {Vocabulary.cancel}
      </Button>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleConfirmRenewal}
        disabled={!selectedSubscription || isProcessing}
        startIcon={isProcessing ? <CircularProgress size={20} /> : <Payment />}
      >
        {isProcessing
          ? Vocabulary.processing
          : isUpgrade
          ? Vocabulary.upgradeNow
          : Vocabulary.renewNow}
      </Button>
    </div>
  );

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={
        isUpgrade
          ? Vocabulary.upgradeSubscription
          : Vocabulary.renewSubscription
      }
      actions={modalActions}
      width="1200px"
      maxWidth={EWidths.LG}
    >
      <Box className={styles.container}>
        {/* Current Subscription Info */}
        {currentSubscription && (
          <Card className={styles.currentSubscriptionCard}>
            <Box className={styles.currentSubscriptionContent}>
              <Typography variant="caption" className={styles.currentLabel}>
                {Vocabulary.currentPlan}
              </Typography>
              <Typography variant="h6" className={styles.subscriptionName}>
                {currentSubscription.name}
              </Typography>
              <Typography variant="body2" className={styles.subscriptionPrice}>
                {currentSubscription.price.toFixed(2)} {Vocabulary.currency}
              </Typography>
            </Box>
          </Card>
        )}

        {/* Available Subscriptions - Reuses SubscriptionSelectionStep */}
        <Box className={styles.subscriptionsContainer}>
          <Typography variant="subtitle2" className={styles.selectTitle}>
            {isUpgrade
              ? Vocabulary.selectUpgradePlan
              : Vocabulary.selectRenewalPlan}
          </Typography>
          <SubscriptionSelectionStep
            subscriptions={availableSubscriptions}
            selectedSubscription={selectedSubscription}
            onSubscriptionSelect={handleSelectSubscription}
            loading={false}
          />
        </Box>

        {/* Price Difference Info */}
        {selectedSubscription &&
          !selectedSubscription._id.includes(currentSubscription?._id || "") &&
          priceDifference > 0 && (
            <Box className={styles.upgradeCostInfo}>
              <Typography variant="body2">
                {Vocabulary.upgradeSubscription}:{" "}
                <strong>
                  +{priceDifference.toFixed(2)} {Vocabulary.currency}
                </strong>
              </Typography>
            </Box>
          )}
      </Box>
    </GenericModal>
  );
}
